import { BasicEvent } from "./Event";


type GetConstructorArgs<T> = T extends new (...args: infer U) => any ? U : never;
type NonFunctionProperties<T> = Pick<T, {
	[K in keyof T]: T[K] extends Function ? never : K;
}[keyof T]>;

type Constructor<T> = { new(...args: any[]): T };
type AbstractConstructor<T> = (abstract new (...args: any[]) => T);
export type AnyConstructor<T> = Constructor<T> | AbstractConstructor<T>;

export type $Proxy<T> = {
	get: () => T;
	set: (variable: T) => void;
	onUpdate: BasicEvent<(value?: T) => void>;
	onChange: BasicEvent<(value?: T) => void>;
};

export namespace ObjectUtils {
	export function deepCopy<T>(input: T) {
		if (input === null || input === undefined) {
			return input as T;
		} else if (typeof input === "object") {
			if (Array.isArray(input)) {
				input = input.map((x) => ObjectUtils.deepCopy(x)) as any;
			} else {
				let output: any = {};
				for (const key in input) {
					if (Object.prototype.hasOwnProperty.call(input, key)) {
						const value = input[key];
						output[key] = ObjectUtils.deepCopy(value);
					}
				}
				input = output;
			}
		}
		return input as T;
	}

	/** Construct an object of type T and assign data to it */
	export function from<T>(
		target: Constructor<T>,
		data: Partial<NonFunctionProperties<T>>,
		...args: GetConstructorArgs<Constructor<T>>
	) {
		return Object.assign(new target(...args), data) as T;
	}

	/** Construct an object of type T and assign JSON data to it */
	export function fromJSON<T>(
		target: Constructor<T>,
		data: string,
		...args: GetConstructorArgs<Constructor<T>>
	) {
		return ObjectUtils.from(target, JSON.parse(data), ...args);
	}

	const proxies = new WeakMap<any, Map<string | number | symbol, any>>();
	export function $<T, K extends keyof T = keyof T>(object: T, key: K): $Proxy<T[K]> {
		let store = proxies.get(object);
		if (store === undefined) {
			proxies.set(object, store = new Map<string | number | symbol, any>());
		}
		let proxy: $Proxy<any> = store.get(key);
		if (proxy === undefined) {
			proxy = {
				get: () => { return object[key]; },
				set: (variable: T[K]) => { object[key] = variable; },
				onUpdate: new BasicEvent<(value?: T[K]) => void>(),
				onChange: new BasicEvent<(value?: T[K]) => void>()
			};
			store.set(key, proxy);
		}
		return proxy;
	}

	export function bind<T>() {
		return (target: T, key: string): void => {

			let descriptor = Object.getOwnPropertyDescriptor(target, key)!;
			if (!descriptor || (typeof descriptor.value !== "function")) {
				throw new Error(`Bind can only be used on function! ${key} is not a function!`);
			}

			return {
				configurable: true,
				get(this: T): T {
					const fnBound: T = ((...args: any[]) => { return descriptor.value.apply(this, args); }) as any as T;

					Object.defineProperty(this, key, {
						value: fnBound,
						configurable: true,
						writable: true
					});
					return fnBound;
				}
			} as any as undefined;
		};
	}

	export function mixin(derived: any, mixed: any[]) {
		mixed.forEach(proto => {
			Object.getOwnPropertyNames(proto.prototype).forEach(name => {
				if (name !== "constructor") {
					let descriptor = Object.getOwnPropertyDescriptor(proto.prototype, name);
					if (descriptor !== undefined) {
						Object.defineProperty(derived.prototype, name, descriptor);
					}
				}
			});
		});
	}
}
